import { QueryCache, QueryClient, type UseQueryResult } from '@tanstack/react-query'

/**
 * One QueryClient for the whole cockpit. Metrics pages read BigQuery marts
 * refreshed hourly, so a 60s staleTime kills refetch storms while keeping
 * ops views current; window-focus refetch keeps an always-open tab honest.
 * Query failures log to the console: pages gate their error banner on "no
 * data yet", so a failed background refetch would otherwise be invisible.
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      console.error(`Query failed [${query.queryKey.join('/')}]:`, error)
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
})

/**
 * Adapt a react-query result to the kit's `{ data, loading, refreshing,
 * error, refetch, updatedAt }` shape the BirdBrain pages consume.
 *
 * `loading` only surfaces REAL fetches: a disabled query reports `isPending`
 * forever, so the `fetchStatus !== 'idle'` guard lives here — the one place —
 * instead of being copy-pasted into every hook wrapper.
 */
export function toKitQuery<T>(query: UseQueryResult<T>) {
  return {
    data: query.data ?? null,
    loading: query.isPending && query.fetchStatus !== 'idle',
    refreshing: query.isFetching && !query.isPending,
    error: query.error,
    refetch: query.refetch,
    updatedAt: query.dataUpdatedAt ? new Date(query.dataUpdatedAt) : null,
  }
}

/**
 * Build a query string from a params object: skips undefined/null/empty
 * strings, joins arrays with commas, stringifies the rest. Callers pass
 * defaults explicitly (page/limit/sort) so the query key and the URL agree.
 */
export function buildSearchParams(
  params: Record<string, string | number | boolean | string[] | undefined | null>
): string {
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      if (value.length === 0) continue
      qs.set(key, value.join(','))
    } else {
      qs.set(key, String(value))
    }
  }
  return qs.toString()
}
